import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';

import {
  EVENT_CATEGORIES,
  EVENT_ACTION_IDS,
  FACEBOOK_EVENTS,
  ANALYTICS_PROVIDERS,
  ANALYTICS_EVENTS,
  ANALYTICS_USER_JOURNEYS
} from 'dpl/constants/analytics';
import {
  trackEvent,
  trackConversion,
  sendFBEvent,
  sendRudderstackEvent
} from 'dpl/util/analytics';
import { isLessThanBreakpoint } from 'dpl/util/grid';
import {
  BreederProfileBreedPropTypes,
  BreederProfileLitterPropTypes,
  BreederProfilePropTypes,
  PuppyPropTypes
} from 'dpl/shared/utils/customPropTypes';
import { memoize } from 'dpl/shared/utils';
import customValidate from 'dpl/shared/validations/customValidate';
import createFormSchema from 'dpl/shared/validations/createFormSchema';
import LoadingWrapper from 'dpl/components/LoadingWrapper';
import IconSet from 'dpl/quiz/adoption_application/components/IconSet';
import BuyerApplicationAuthModal from 'dpl/buyer_account/components/BuyerApplicationAuthModal';
import { VALIDATION_SCHEMA } from 'dpl/buyer_account/components/AdditionalInfoForm';
import isFeatureEnabled from 'dpl/util/isFeatureEnabled';
import { FEATURES } from 'dpl/shared/constants/features';

import AdoptionApplicationContext from '../utils/context';
import QuizContainer from '../containers/QuizContainer';
import IntroOverlay from './IntroOverlay';
import AdditionalInfoFormModal from './AdditionalInfoFormModal';

const IS_LT_MD = isLessThanBreakpoint('md');

const ADDITIONAL_INFO_VALIDATION_SCHEMA = createFormSchema(VALIDATION_SCHEMA);

const BUYER_AUTH_REMOVAL_ENABLED = isFeatureEnabled(
  FEATURES.RELEASE_BUYER_APPLICATION_AUTH_REMOVAL
);

const didCompleteAdditionalInfoForm = memoize(
  buyerProfileData => {
    const errors = customValidate(
      buyerProfileData.value.buyer_profile,
      ADDITIONAL_INFO_VALIDATION_SCHEMA,
      { cleanAttributes: false }
    );

    return !errors;
  },
  buyerProfileData => buyerProfileData.meta.lastUpdated
);

export default class ApplicationLayout extends Component {
  static propTypes = {
    isFormValidating: PropTypes.bool.isRequired,
    // eslint-disable-next-line react/forbid-prop-types
    formErrors: PropTypes.object,
    // eslint-disable-next-line react/forbid-prop-types
    formState: PropTypes.object.isRequired,
    getFormErrors: PropTypes.func.isRequired,
    handleFormFieldChange: PropTypes.func.isRequired,
    addToFormState: PropTypes.func.isRequired,
    // eslint-disable-next-line react/forbid-prop-types
    allUploadingUploads: PropTypes.object.isRequired,
    handleUploadInputChange: PropTypes.func.isRequired,
    handleAllUploadError: PropTypes.func.isRequired,
    handleAllUploadSuccess: PropTypes.func.isRequired,
    handleFormSubmission: PropTypes.func.isRequired,
    isLoadingData: PropTypes.bool.isRequired,
    // eslint-disable-next-line react/forbid-prop-types
    buyerProfileData: PropTypes.object.isRequired,
    // eslint-disable-next-line react/forbid-prop-types
    buyerApplicationData: PropTypes.object.isRequired,
    breederProfileData: PropTypes.shape({
      value: PropTypes.shape(BreederProfilePropTypes)
    }).isRequired,
    availableBreeds: PropTypes.arrayOf(
      PropTypes.shape(BreederProfileBreedPropTypes)
    ).isRequired,
    availablePuppies: PropTypes.arrayOf(PropTypes.shape(PuppyPropTypes))
      .isRequired,
    availableLitters: PropTypes.arrayOf(
      PropTypes.shape(BreederProfileLitterPropTypes)
    ).isRequired,
    canOverrideAvailability: PropTypes.bool.isRequired,
    reservedBreeds: PropTypes.arrayOf(
      PropTypes.shape(BreederProfileBreedPropTypes)
    ).isRequired,
    onCloseClick: PropTypes.func.isRequired,
    isFormSubmitting: PropTypes.bool.isRequired,
    didFormSubmissionFail: PropTypes.bool.isRequired,
    litterApplyingTo: PropTypes.shape({
      id: PropTypes.number
    }).isRequired,
    puppyApplyingTo: PropTypes.shape({
      id: PropTypes.number
    }).isRequired,
    resetFormState: PropTypes.func.isRequired,
    currentUser: PropTypes.shape({
      fetch: PropTypes.func.isRequired
    }).isRequired,
    isLoggedIn: PropTypes.bool,
    // eslint-disable-next-line react/forbid-prop-types
    availableDeliveryMethods: PropTypes.object.isRequired,
    pushToFormState: PropTypes.func.isRequired,
    removeFromFormState: PropTypes.func.isRequired,
    batchAddToFormState: PropTypes.func.isRequired,
    adoptableBreedName: PropTypes.string
  };

  static defaultProps = {
    formErrors: {},
    isLoggedIn: null,
    adoptableBreedName: null
  };

  static getDerivedStateFromProps(nextProps, prevState) {
    const { buyerApplicationData, breederProfileData } = nextProps;

    const newState = {
      breederName: breederProfileData.value.user.first_name,
      breederSlug: breederProfileData.value.slug,
      breederProfilePhotoUrl: breederProfileData.value.profile_photo_url,
      breederProfileUrl: breederProfileData.value.profile_url,
      didStartQuiz:
        prevState.didStartQuiz ||
        Boolean(buyerApplicationData.value.buyer_application.id),
      photoUrl: breederProfileData.value.profile_photo_url,
      programName: breederProfileData.value.name,
      titleName: 'Puppy'
    };

    return newState;
  }

  static contextType = AdoptionApplicationContext;

  state = {
    didStartQuiz: false
  };

  handleStartQuizClick = () => {
    this.setState({
      didStartQuiz: true
    });
  };

  handleSubmit = compose(
    trackEvent(EVENT_CATEGORIES.SUBMIT_APPLICATION, 'submit', null, [
      ANALYTICS_PROVIDERS.GA,
      ANALYTICS_PROVIDERS.AHOY,
      ANALYTICS_PROVIDERS.BING
    ]),
    trackConversion(EVENT_ACTION_IDS.SUBMIT_APPLICATION)
  )(() => {
    const { buyer_application: buyerApplication } =
      this.props.buyerApplicationData.value;
    const breederProfile = this.props.breederProfileData.value;

    // TODO: consolidate event category and use `trackEvent`
    sendFBEvent(FACEBOOK_EVENTS.SUBMIT_APPLICATION);

    sendRudderstackEvent(ANALYTICS_EVENTS.BUYER_APP_SUBMITTED, {
      buyer_application_id: buyerApplication.id,
      buyer_profile_id: buyerApplication.buyer_profile_attributes.id,
      breeder_preferred_status: breederProfile.preferred_qualification?.status,
      breeder_profile_id: buyerApplication.breeder_profile_id,
      breed_id: buyerApplication.breed_id,
      litter_id: buyerApplication.litter_id,
      puppy_id: buyerApplication.dog_id,
      user_journey: ANALYTICS_USER_JOURNEYS.BUYER_ACTIVATION
    });

    return this.props.handleFormSubmission(true);
  });

  handleBeforeNext = async () => {
    const { setIsSubmittingAndRefetching } = this.context;
    setIsSubmittingAndRefetching(true);
    await this.props.handleFormSubmission(false);
    await this.props.buyerApplicationData.fetch();
    setIsSubmittingAndRefetching(false);
  };

  render() {
    const {
      formErrors,
      formState,
      getFormErrors,
      handleFormFieldChange,
      addToFormState,
      resetFormState,
      isFormSubmitting,
      didFormSubmissionFail,
      handleFormSubmission,
      handleAllUploadSuccess,
      handleAllUploadError,
      allUploadingUploads,
      handleUploadInputChange,
      isLoadingData,
      availableBreeds,
      availableLitters,
      buyerApplicationData,
      availablePuppies,
      canOverrideAvailability,
      reservedBreeds,
      onCloseClick,
      litterApplyingTo,
      puppyApplyingTo,
      currentUser,
      isLoggedIn,
      availableDeliveryMethods,
      pushToFormState,
      removeFromFormState,
      isFormValidating,
      batchAddToFormState,
      buyerProfileData,
      adoptableBreedName
    } = this.props;

    const {
      breederName,
      breederSlug,
      breederProfilePhotoUrl,
      didStartQuiz,
      photoUrl,
      programName,
      titleName
    } = this.state;

    const { isFetching: isBuyerProfileDataFetching } = buyerProfileData.meta;

    const shouldShowAdditionalInfoForm =
      isLoggedIn &&
      !isBuyerProfileDataFetching &&
      !didCompleteAdditionalInfoForm(buyerProfileData);

    return (
      <div className="AdoptionApplication min-vh-100 bg-white">
        {/*
            only show spinner if not already in the quiz flow;
            otherwise it'll skip to the next step without smooth transition
        */}
        <LoadingWrapper
          isLoading={!didStartQuiz ? isLoadingData : isBuyerProfileDataFetching}
          className="transform-center-all"
        >
          {(() => {
            if (didStartQuiz) {
              if (!BUYER_AUTH_REMOVAL_ENABLED) {
                if (isLoggedIn === false) {
                  return (
                    <BuyerApplicationAuthModal
                      onCloseClick={onCloseClick}
                      onSuccessfulAuth={buyerApplicationData.fetch}
                    />
                  );
                }

                if (shouldShowAdditionalInfoForm) {
                  return (
                    <AdditionalInfoFormModal
                      onCloseClick={onCloseClick}
                      onSuccessfulSubmission={buyerApplicationData.fetch}
                    />
                  );
                }
              }

              return (
                <QuizContainer
                  isFormValidating={isFormValidating}
                  formErrors={formErrors}
                  formState={formState}
                  resetFormState={resetFormState}
                  currentUser={currentUser}
                  getFormErrors={getFormErrors}
                  handleFormFieldChange={handleFormFieldChange}
                  addToFormState={addToFormState}
                  isFormSubmitting={isFormSubmitting}
                  didFormSubmissionFail={didFormSubmissionFail}
                  handleAllUploadSuccess={handleAllUploadSuccess}
                  handleAllUploadError={handleAllUploadError}
                  allUploadingUploads={allUploadingUploads}
                  handleUploadInputChange={handleUploadInputChange}
                  handleFormSubmission={handleFormSubmission}
                  onSubmit={this.handleSubmit}
                  onBeforeNext={this.handleBeforeNext}
                  buyerApplicationData={buyerApplicationData}
                  availableBreeds={availableBreeds}
                  availableLitters={availableLitters}
                  availablePuppies={availablePuppies}
                  canOverrideAvailability={canOverrideAvailability}
                  reservedBreeds={reservedBreeds}
                  onCloseClick={onCloseClick}
                  litterApplyingTo={litterApplyingTo}
                  puppyApplyingTo={puppyApplyingTo}
                  programName={programName}
                  breederName={breederName}
                  breederSlug={breederSlug}
                  breederProfilePhotoUrl={breederProfilePhotoUrl}
                  availableDeliveryMethods={availableDeliveryMethods}
                  pushToFormState={pushToFormState}
                  removeFromFormState={removeFromFormState}
                  fixedNav={IS_LT_MD}
                  batchAddToFormState={batchAddToFormState}
                />
              );
            }

            return (
              <IntroOverlay
                onCloseClick={onCloseClick}
                onStartClick={this.handleStartQuizClick}
                breederName={breederName}
                breederSlug={breederSlug}
                programName={programName}
                photoUrl={photoUrl}
                titleName={titleName}
                buyerApplicationData={buyerApplicationData}
                fixedNav={IS_LT_MD}
                puppyApplyingTo={puppyApplyingTo}
                litterApplyingTo={litterApplyingTo}
                adoptableBreedName={adoptableBreedName}
              />
            );
          })()}
          <IconSet />
          {
            // HACK: addresses a bug in ReactModal where rendering a modal w/in
            // another modal, doesn't allow tabbing between input fields
          }
          <a
            href="#"
            onClick={e => e.preventDefault()}
            className="absolute o-0 h0 w0"
          >
            &#8203;
          </a>
        </LoadingWrapper>
      </div>
    );
  }
}
